import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../store/store.ts";
import { IService } from "../../type/type.ts";
import { fetchServices, addService, updateService, updateStatus, deleteService } from "../../slices/ServiceMasterSlice.ts";
import Searchbar from "../../components/common/Searchbar.tsx";
import SideSheet from "../../components/common/Sidesheet.tsx";
import CustomTable from "../../components/common/Table.tsx";
import { Pagination, Switch } from "antd";
import * as yup from "yup";
import { Formik, Form, useFormik } from "formik";
import FormInput from "../../components/common/FormInput.tsx";
import { Image } from "antd";
import Confirmation from "../../components/common/Confirmation.tsx";
import { notification } from "antd";
import { capitalizeFirstLetter } from "../../utils/util.service.ts";
import { setOffset, setLimit, setSortField, setSortOrder } from "../../slices/ServiceMasterSlice.ts";
import RequiredLabel from "../../components/common/RequiredLabel.tsx";
import { setBreadcrumbs } from "../../slices/BreadcrumbSlice.ts";


const ServiceMaster = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { services, loading, dataCount, offset, limit } = useSelector((state: RootState) => state.serviceMaster);

  const [selectedService, setSelectedService] = useState<IService | null>(null);
  const [query, setQuery] = useState("");
  const [isSideSheetOpen, setSideSheetOpen] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [confirmationMessage, setConfirmationMessage] = useState("Do you want to submit the form?");
  const [sortedData, setSortedData] = useState<IService[]>([]);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    order: "ascend" | "descend" | null;
  }>({
    key: "",
    order: null,
  });
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState<IService | null>(null);

  useEffect(() => {
    dispatch(setBreadcrumbs([
      {
        title: "Service Master",
        path: '/masters/service-master',

      }
    ]));

  }, [dispatch]);




  useEffect(() => {
    dispatch(fetchServices({
      offset: offset,
      limit: limit,
      sortField: sortConfig.key ?? null,
      sortOrder: sortConfig.order && sortConfig.order === 'ascend' ? 1 : sortConfig.order === 'descend' ? -1 : null,
      searchInput: query
    }));
  }, [dispatch, offset, limit, sortConfig, query]);




  const showNotification = (type: "success" | "error", message: string) => {
    notification[type]({
      message,
      placement: "top",
    });
  };

  const handleSearch = (value: string) => {
    console.log(value, 'value');

    setQuery(value);
    dispatch(setOffset(0));
  };

  const handlePaginationChange = (page: number, newPageSize?: number) => {
    console.log({ offset, limit, dataCount });
    dispatch(setOffset(page - 1));  
    if (newPageSize && newPageSize !== limit) {
      dispatch(setLimit(newPageSize));
    }
  };

  const handleSortChange = (_: any, __: any, sorter: any) => {
    if (Array.isArray(sorter)) {
      sorter = sorter[0];
    }

    const { field, order } = sorter || {};

    const sortOrder = order === 'ascend' ? 1 : -1;

    setSortConfig({
      key: field || null,
      order: order || null
    });

    dispatch(setSortField(field));
    dispatch(setSortOrder(sortOrder));

    dispatch(setOffset(offset));
  };

  // const handleStatusChange = async (id: number, currentStatus: boolean) => {
  //   try {
  //     await dispatch(updateStatus({ id, isActive: !currentStatus })).unwrap();
  //     showNotification("success", "Status updated successfully");
  //     dispatch(fetchServices({
  //       searchInput: query,
  //       offset: offset,
  //       limit: limit,
  //       sortField: sortConfig.key ?? null,
  //       sortOrder: sortConfig.order && sortConfig.order === 'ascend' ? 1 : sortConfig.order === 'descend' ? -1 : null
  //     }));
  //   } catch (error) {
  //     showNotification("error", "Failed to update status");
  //   }
  // };


  const handleEditClick = (service: IService) => {
    setSelectedService(service);
    setSideSheetOpen(true);
  };

  const handleDeleteClick = (service: IService) => {
    setServiceToDelete(service);
    setConfirmationMessage("Are you sure you want to delete this service?");
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!serviceToDelete) return;

    try {
      await dispatch(deleteService(serviceToDelete.id)).unwrap();
      showNotification("success", "Service deleted successfully");
      dispatch(fetchServices({
        searchInput: query,
        offset: offset,
        limit: limit,
        sortField: sortConfig.key ?? null,
        sortOrder: sortConfig.order && sortConfig.order === 'ascend' ? 1 : sortConfig.order === 'descend' ? -1 : null
      }));
    } catch (error) {
      showNotification("error", "Failed to delete service");
    } finally {
      setIsDeleteConfirmationOpen(false);
      setServiceToDelete(null);
    }
  };

  const columns = [
    {
      title: <span className="font-montserrat font-medium text-[13px] leading-5 tracking-normal text-[#6D6D6D] capitalize">S.No</span>,
      dataIndex: "serialNumber",
      key: "serialNumber",
      render: (_: any, __: any, index: number) =>
        (offset * limit + index + 1).toString().padStart(2, "0"),
      width: 50,
    },
    {
      title: <span className="font-montserrat font-medium text-[13px] leading-5 tracking-normal text-[#6D6D6D] capitalize">Service Name</span>,
      dataIndex: "name",
      key: "name",
      sorter: true,
      sortDirections: ["ascend", "descend"],
      sortOrder: sortConfig.key === "name" ? sortConfig.order : null,
      render: (name: string) => capitalizeFirstLetter(name),
    },
    {
      title: <span className="font-montserrat font-medium text-[13px] leading-5 tracking-normal text-[#6D6D6D] capitalize">Date of Creation</span>,
      dataIndex: "created_at",
      key: "created_at",
      sorter: true,
      sortDirections: ["ascend", "descend"],
      sortOrder: sortConfig.key === "created_at" ? sortConfig.order : null,
      render: (created_at: string) =>
        new Date(created_at).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }),
    },
    {
      title: <span className="text-[#6D6D6D]">Action</span>,
      dataIndex: 'action',
      key: 'action',
      sorter: false,
      render: (_: any, record: IService) => (
        <div className="flex items-center gap-3">
          <img
            src="/client/edit.svg"
            alt="Edit"
            className="w-5 h-5 cursor-pointer hover:scale-105 transition-transform"
            onClick={() => handleEditClick(record)}
          />
          <img
            src="/client/delete.svg"
            alt="Delete"
            className="w-5 h-5 cursor-pointer hover:scale-105 transition-transform"
            onClick={() => handleDeleteClick(record)}
          />
        </div>
      )
    }
  ];

  const validationSchema = yup.object({
    serviceName: yup
      .string()
      .required("service name is required")
      .min(2, "service name must be at least 2 characters")
      .max(50, "service name must not exceed 50 characters")
      .matches(
        /^(?![0-9]+$)[a-zA-Z0-9 _-]+$/,
        "Service name can only contain letters, numbers, spaces, underscores (_), hyphens (-), and cannot be empty."),
  });

  return (
    <>
    <Formik
      enableReinitialize
      initialValues={{
        serviceName: capitalizeFirstLetter(selectedService?.name || ""),
      }}
      validationSchema={validationSchema}
      validateOnChange={true}
      validateOnBlur={false}
      onSubmit={(values, { resetForm }) => {
        setConfirmationMessage(
          selectedService ? "Do you want to update the service?" : "Do you want to submit the service?"
        );
        setIsConfirmationOpen(true);
  
        setConfirmAction(() => async () => {
          try {
            if (selectedService) {
              await dispatch(
                updateService({
                  id: selectedService.id,
                  name: values.serviceName,
                })
              ).unwrap();
  
              showNotification("success", "Service updated successfully.");
            } else {
              await dispatch(
                addService({ name: values.serviceName })
              ).unwrap();
  
              showNotification("success", "Service added successfully.");
            }
  
            await dispatch(
              fetchServices({
                searchInput: query,
                offset,
                limit,
                sortField: "",
                sortOrder: null,
              })
            );
  
            resetForm();
            setSideSheetOpen(false);
            setSelectedService(null);
          } catch (error) {
            showNotification("error", error?.message || "Operation failed.");
          }
  
          setIsConfirmationOpen(false);
        });
      }}
    >
      {({
        handleSubmit,
        resetForm,
        values,
        errors,
        touched,
        handleChange,
        setFieldTouched,
        validateField,
      }) => (
        <>
          <div className="bg-[#EDF1F6] h-[76px] mt-[10px] flex justify-between items-center px-[20px]">
            <p className="font-Montserrat font-semibold text-[20px] leading-[24.38px] text-[#0A0A0A]">
              Service Master
            </p>
            <div className="flex justify-end gap-3">
              <Searchbar search={query} onSearch={handleSearch} />
  
              <SideSheet
                btnLabel={selectedService ? "Edit Service" : "Add Service"}
                width={443}
                title={selectedService ? "Edit Service" : "Add Service"}
                isOpen={isSideSheetOpen}
                setOpen={setSideSheetOpen}
                onSubmit={handleSubmit}
                submitLabel={selectedService ? "Update" : "Submit"}
                onCancel={() => {
                  resetForm();
                  setSideSheetOpen(false);
                  setSelectedService(null);
                }}
              >
                <Form>
                  <div className="mb-8">
                    <RequiredLabel>Service Name</RequiredLabel>
                    <FormInput
                      name="serviceName"
                      type="text"
                      placeholder="Enter Service name"
                      onChange={(e) => {
                        handleChange(e);
                        setFieldTouched("serviceName", true, false);
                        validateField("serviceName");
                      }}
                      value={values.serviceName}
                      error={
                        touched.serviceName && errors.serviceName
                          ? errors.serviceName
                          : ""
                      }
                    />
                  </div>
                </Form>
              </SideSheet>
            </div>
          </div>
  
          <div className="flex flex-col h-[85vh]">
            <div className="flex-1">
              <CustomTable<IService>
                columns={columns}
                dataSource={services}
                loading={loading}
                rowKey="id"
                onChange={handleSortChange}
              />
            </div>
            <div className="sticky bottom-0 flex justify-end py-4">
              <Pagination
                total={dataCount}
                current={offset + 1}
                pageSize={limit}
                showSizeChanger={false}
                onChange={handlePaginationChange}
              />
            </div>
          </div>
  
          <Confirmation
            label="Confirmation"
            message={confirmationMessage}
            isOpen={isConfirmationOpen || isDeleteConfirmationOpen}
            onClose={() => {
              setIsConfirmationOpen(false);
              setIsDeleteConfirmationOpen(false);
              setServiceToDelete(null);
            }}
            onConfirm={
              isDeleteConfirmationOpen
                ? handleDeleteConfirm
                : confirmAction || (() => {})
            }
            confirmButtonLabel="Confirm"
          />
        </>
      )}
    </Formik>
  </>
  
  );
};

export default ServiceMaster;
